import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    CeButtonComponent,
    CeIconComponent,
    CeLinkButtonComponent,
    CeParameterListImports,
    CePillComponent,
    CeSnackbarService,
    CeSnackbarState,
    CeSpinningLoaderComponent,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { FindReceiptsResponseItem } from '@kempoweroyj/ngx-receipts-api-client';
import { ConfigService } from '@kempower-nx-workspace/receipt-lookup-environment';
import {
    convertDurationMsToDisplay,
    convertIsoDateToDisplay,
    convertSessionStartedToDisplay,
} from '../../utils/date-utils';

export const SAFE_TRANSPARENCY_URL = 'https://www.safe-ev.org';

@Component({
    selector: 'lib-found-receipt',
    imports: [
        CommonModule,
        CeParameterListImports,
        CeIconComponent,
        CeButtonComponent,
        CeLinkButtonComponent,
        CePillComponent,
        CeSpinningLoaderComponent,
    ],
    templateUrl: './found-receipt.component.html',
    styleUrl: './found-receipt.component.scss'
})
export class FoundReceiptComponent {
    receipt = input<FindReceiptsResponseItem | null>(null);
    isDownloading = signal<boolean>(false);

    readonly #http = inject(HttpClient);
    readonly #snackbar = inject(CeSnackbarService);
    readonly #config = inject(ConfigService);

    readonly safeTransparencyUrl = SAFE_TRANSPARENCY_URL;

    displayDate = computed(() => convertIsoDateToDisplay(this.receipt()?.date));
    displaySessionStarted = computed(() =>
        convertSessionStartedToDisplay(this.receipt()?.sessionStarted, this.receipt()?.stationTimezone)
    );
    displayDuration = computed(() => convertDurationMsToDisplay(this.receipt()?.durationMs));
    hasSignedMeterValues = computed(() => !!this.receipt()?.hasSignedMeterValues);

    downloadReceipt() {
        if (!this.receipt() || !this.receipt()?.downloadUrl) {
            return;
        }

        this.isDownloading.set(true);
        const downloadUrl = `${this.receipt()!.downloadUrl}&asPdf=true`;
        const filename = `receipt-${this.receipt()?.txId || 'download'}.pdf`;

        this.#downloadFileFromUrl(downloadUrl, filename);
    }

    downloadXML() {
        const receipt = this.receipt();
        if (!receipt?.eMeterId || !receipt?.txId) {
            return;
        }
        const url = `${this.#config.restApiUrl}/tx/eichrecht/${encodeURIComponent(receipt.eMeterId)}/${encodeURIComponent(receipt.txId)}`;
        this.#http.get<{ xml: string }>(url).subscribe({
            next: (res) => {
                const blob = new Blob([res.xml], { type: 'application/xml' });
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `signed-meter-values-${receipt.txId}.xml`;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(blobUrl);
            },
            error: () => {
                this.#snackbar.showSnackbar({
                    message: 'Error downloading signed meter values',
                    state: CeSnackbarState.ERROR,
                    icon: 'error',
                    showCloseButton: true,
                });
            },
        });
    }

    #downloadFileFromUrl(url: string, filename: string) {
        // Create a hidden link element and trigger download
        // This approach handles redirects automatically
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);

        link.addEventListener('click', () => {
            setTimeout(() => {
                this.isDownloading.set(false);
                document.body.removeChild(link);
            }, 1000);
        });

        link.addEventListener('error', () => {
            this.#snackbar.showSnackbar({
                message: 'Error downloading the receipt',
                state: CeSnackbarState.ERROR,
                icon: 'error',
                showCloseButton: true,
            });
            this.isDownloading.set(false);
            document.body.removeChild(link);
        });

        link.click();
    }
}
